// src/App.jsx
import AppShell from './ui/AppShell';
import React from 'react';
import ContactOverlay from './components/ContactOverlay';
import IdleLogout from './components/IdleLogout';
import ChatBotWidget from './components/ChatBotWidget';
import api, { clearTokens, setTokens } from './api';
import { Routes, Route, Link, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';

import Login from './pages/Login';
import Forgot from './pages/Forgot';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import TenantOnboard from './pages/TenantOnboard';
import Customers from './pages/Customers';
import Products from './pages/Products';
import POS from './pages/POS';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import License from './pages/License';
import Roles from './pages/Roles';
import Users from './pages/Users';
import { hasPerm } from './utils/perm';
import Sales from './pages/Sales';
import Invoice from './pages/Invoice';
import Credit from './pages/Credit';
import SalesDetail from './pages/SalesDetail';
import Returns from './pages/Returns';
import Logging from './pages/Logging';
import { ProtectedRoute } from './components/Protected';
import Welcome from './pages/Welcome';
import Home from './pages/Home';
import Privacy from './pages/Privacy';
import DeleteAccount from './pages/DeleteAccount';

function sanitizeStateCode(v) {
  return String(v || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
}
function isProfileComplete(data) {
  const name = String(data?.shop_name || '').trim();
  const state = sanitizeStateCode(data?.state_code ?? data?.origin_state ?? '');
  return !!(name && /^[A-Z]{2}$/.test(state));
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}

function ProfileGate({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = React.useState('checking'); // checking | ok | need

  React.useEffect(() => {
    let cancelled = false;
    const cached = localStorage.getItem('shop_profile_complete');
    if (cached === '1') setStatus('ok');
    else if (cached === '0') setStatus('need');
    else setStatus('checking');

    (async () => {
      try {
        const { data } = await api.get('/settings');
        const complete = isProfileComplete(data);
        if (!cancelled) {
          setStatus(complete ? 'ok' : 'need');
          try { localStorage.setItem('shop_profile_complete', complete ? '1' : '0'); } catch {}
        }
      } catch {
        // If server is busy, do not trap the user. Keep whatever cached status is.
        if (!cancelled && status === 'checking') setStatus('ok');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (status === 'checking') return;
    if (status === 'need' && location.pathname !== '/welcome') {
      navigate('/welcome', { replace: true });
    } else if (status === 'ok' && location.pathname === '/welcome') {
      if (localStorage.getItem('shop_profile_complete') === '1') navigate('/dashboard', { replace: true });
    }
  }, [status, location.pathname, navigate]);

  if (status === 'checking') return null;
  return children;
}

/** Read user display name from localStorage to show in header */
function getDisplayName() {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user') || '{}';
    const u = JSON.parse(raw);
    if (u && typeof u === 'object') {
      if (u.name && u.name.trim()) return u.name.trim();
      if (u.email && u.email.trim()) return u.email.trim();
    }
  } catch {}
  return 'Account';
}

// ---- icons
function IconSettings(props){return (      
<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="2"/><path d="M19.4 15a7.97 7.97 0 0 0 .1-2l2.1-1.6-2-3.4-2.5.5a8 8 0 0 0-1.7-1l-.4-2.6h-4l-.4 2.6a8 8 0 0 0-1.7 1l-2.5-.5-2 3.4L4.5 13c-.1.7-.1 1.3 0 2l-2.1 1.6 2 3.4 2.5-.5c.5.4 1.1.7 1.7 1l.4 2.5h4l.4-2.5c.6-.3 1.2-.6 1.7-1l2.5.5 2-3.4L19.4 15Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>);}
function IconUsers(props){return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="7" r="3" stroke="currentColor" strokeWidth="2"/><path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2"/><path d="M17 11a3 3 0 0 0 0-6" stroke="currentColor" strokeWidth="2"/></svg>);}
function IconShield(props){return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}><path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>);}
function IconFileText(props){return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z" stroke="currentColor" strokeWidth="2"/><path d="M14 2v6h6" stroke="currentColor" strokeWidth="2"/><path d="M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);}
function IconPhone(props){return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.61a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.47-1.14a2 2 0 0 1 2.11-.45c.84.29 1.71.5 2.61.62A2 2 0 0 1 22 16.92Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>);}
function IconLogout(props){return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M16 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);}

function Layout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // click-away / Esc to close the account dropdown
  const menuRef = React.useRef(null);
  const [contactOpen, setContactOpen] = React.useState(false);
  React.useEffect(() => {
    function onDocClick(e) {
      const el = menuRef.current;
      if (!el) return;
      if (el.hasAttribute('open') && !el.contains(e.target)) {
        el.removeAttribute('open');
      }
    }
    function onEsc(e) {
      if (e.key === 'Escape') menuRef.current?.removeAttribute('open');
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const logout = async () => {
    const r = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
    try { if (r) await api.post('/auth/logout', { refreshToken: r }); } catch (e) {}
    clearTokens();
    navigate('/login', { replace: true });
  };

  const isActive = (to) => {
    if (to === '/dashboard') return pathname === '/dashboard';
    return pathname.startsWith(to);
  };

  const Tab = ({ to, label, type = 'normal' }) => {
    const cls = [
      'tab-pill',
      type === 'quick' ? 'quick' : '',
      isActive(to) ? 'active' : ''
    ].filter(Boolean).join(' ');
    return (
      <Link to={to} style={{ textDecoration: 'none' }}>
        <span className={cls}>{label}</span>
      </Link>
    );
  };

  const canSeeUsers = hasPerm('users:read');
  const canSeeRoles = hasPerm('roles:read') || hasPerm('users:read');

  return (
    <div>
      <header id="topbar" role="banner">
        <div className="topbar-inner">
          <div style={{ minHeight: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', flexWrap:'wrap' }}>
            {/* Left: Logo */}
            <div className="brand-wrap topbar-brand" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img
                src="/brand/sellantra-mark.png"    // place file in public/brand/
                alt="Sellantra"
                width={32}
                height={32}
                style={{ display: 'block', objectFit: 'contain' }}
                onError={(e) => {
                  // fallback to the old blue dot if file missing
                  e.currentTarget.outerHTML =
                    '<div style="width:32px;height:32px;border-radius:999px;background:linear-gradient(135deg,#2563eb,#60a5fa);box-shadow:0 4px 10px rgba(37,99,235,.3)"></div>';
                }}
              />
              <div style={{ fontWeight: 700, letterSpacing: '-0.01em', fontSize: 18, color: 'var(--muted)' }}>
                Sellantra
              </div>
            </div>

            {/* Center: Primary Nav */}
            <nav className="topbar-nav" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tab to="/dashboard"  label="Dashboard" />
              <Tab to="/customers"  label="Customers" />
              <Tab to="/products"   label="Products" />
              <Tab to="/sales"      label="Sales" />
              <Tab to="/invoice"    label="Invoice" />
              <Tab to="/credit"     label="Credit" />
            <Tab to="/reports"    label="Reports" />
            <Tab to="/pos"        label="POS"     type="quick" />
              <Tab to="/returns"    label="Returns" type="quick" />
            </nav>

            {/* Right: Account dropdown */}
            <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <details ref={menuRef} style={{ position: 'relative' }}>
                <summary
                  style={{
                    cursor: 'pointer',
                    listStyle: 'none',
                    padding: '6px 10px',
                    border: '1px solid var(--hairline)',
                    borderRadius: 999,
                    fontWeight: 600,
                    color: 'var(--fg)',
                    background: 'transparent',
                  }}
                >
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                      <path d="M4 22a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {getDisplayName()} ▾
                  </span>
                </summary>

                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 6px)',
                    background: 'var(--card)',
                    border: '1px solid var(--hairline)',
                    borderRadius: 10,
                    minWidth: 220,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
                    padding: 8,
                    zIndex: 50,
                    color: 'var(--fg)',
                  }}
                >
                  <div style={{ display: 'grid', gap: 8 }}>
                    <Link
                      to="/settings"
                      style={{ textDecoration:'none', color:'var(--fg)', fontWeight:600 }}
                      onClick={() => menuRef.current?.removeAttribute('open')}
                    >
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><IconSettings/> Settings</span>
                    </Link>
                    <Link
                      to="/logging"
                      style={{ textDecoration:'none', color:'var(--fg)', fontWeight:600 }}
                      onClick={() => menuRef.current?.removeAttribute('open')}
                    >
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><IconFileText/> Logging</span>
                    </Link>

                    {canSeeUsers && (
                      <Link
                        to="/users"
                        style={{ textDecoration:'none', color:'var(--fg)', fontWeight:600 }}
                        onClick={() => menuRef.current?.removeAttribute('open')}
                      >
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><IconUsers/> Users</span>
                      </Link>
                    )}
                    {canSeeRoles && (
                      <Link
                        to="/roles"
                        style={{ textDecoration:'none', color:'var(--fg)', fontWeight:600 }}
                        onClick={() => menuRef.current?.removeAttribute('open')}
                      >
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><IconShield/> Roles</span>
                      </Link>
                    )}

                    <hr style={{ border: 0, borderTop: '1px solid var(--hairline)', margin: '4px 0' }} />
                    <button
                      type="button"
                      style={{ textDecoration:'none', color:'var(--fg)', fontWeight:600, background:'transparent', border:'none', padding:0, cursor:'pointer' }}
                      onClick={() => { menuRef.current?.removeAttribute('open'); setContactOpen(true); }}
                    >
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}><IconPhone/> Contact Us</span>
                    </button>
                    <a
                      href="/license"
                      style={{ textDecoration:'none', color:'var(--fg)', fontWeight:600 }}
                      onClick={() => menuRef.current?.removeAttribute('open')}
                    >
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>🪪 License</span>
                    </a>
                    <hr style={{ border: 0, borderTop: '1px solid var(--hairline)', margin: '4px 0' }} />
                    <button
                      onClick={() => { menuRef.current?.removeAttribute('open'); logout(); }}
                      style={{
                        textAlign: 'left',
                        background: 'transparent',
                        border: 0,
                        color: 'var(--danger)',
                        padding: 0,
                        cursor: 'pointer',
                        fontWeight: 600,
                        display: 'inline-flex',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <IconLogout/> Logout
                    </button>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
      </header>

      <main>
        <Outlet />
      </main>
      <ContactOverlay open={contactOpen} onClose={() => setContactOpen(false)} />
      <ChatBotWidget />
    </div>
  );
}

function App() {
  const canSeeRoles = hasPerm('roles:read') || hasPerm('users:read');

  return (
    <AppShell>
      <>
        <IdleLogout />
        <Routes>
          {/* public */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />

          {/* profile capture page: auth required but outside chrome to avoid dashboard data fetches */}
          <Route
            path="/welcome"
            element={
              <PrivateRoute>
                <Welcome />
              </PrivateRoute>
            }
          />

          {/* protected chrome (ALL pages inside Layout) */}
          <Route
            element={
              <PrivateRoute>
                <ProfileGate>
                  <Layout />
                </ProfileGate>
              </PrivateRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/products" element={<Products />} />
            <Route path="/pos" element={<POS />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/invoice" element={<Invoice />} />
            <Route path="/credit" element={<Credit />} />
            <Route path="/sales/:id" element={<SalesDetail />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/logging" element={<Logging />} />

            {/* RBAC pages inside chrome */}
            <Route path="/users" element={<ProtectedRoute perm="users:read" element={<Users />} />} />
            <Route path="/roles" element={canSeeRoles ? <Roles /> : <Navigate to="/dashboard" replace />} />
            <Route path="/license" element={<License />} />
          </Route>

          <Route path="/onboard" element={<TenantOnboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    </AppShell>
  );
}

export default App;
